import { Avatar, Box, IconButton, Menu, MenuItem } from '@mui/material'
import { useRef, useState } from 'react'
import { PrimaryDots } from '../../assets/images'
import { VenderMenuProps } from '../../interfaceTypes/props'
import { anchorOrigin, transformOrigin } from '../../theme/styleConstants'
import VenderModal from '../vendors/VenderModal'
import { ConfirmationModal } from '../common/ConfirmationModal'
import { useDeactivateVendorMutation, useReactivateVendorMutation } from '../../../generated'
import { Alert } from '../common/Alert'

const VendorMenu = ({ vendor, refetch }: VenderMenuProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [open, setOpen] = useState(false)
  const [confirmDeactivate, setConfirmDeactivate] = useState(false)
  const [confirmReactivate, setConfirmReactivate] = useState(false)
  const { id } = vendor

  const isInactive = Boolean(vendor?.deletedAt)

  const handleClick = () => setOpen(!open)
  const handleClose = () => setOpen(false)

  const anchorRef = {
    [id]: useRef<HTMLButtonElement>(null)
  }

  const handleEdit = () => setIsEditOpen(true)

  const handleEditClose = () => {
    handleClose()
    setIsEditOpen(false)
  }

  const [deactivateVendor, { loading: deactivating }] = useDeactivateVendorMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted() {
      Alert.success('Vendor deactivated')
      refetch?.()
      setConfirmDeactivate(false)
    }
  })

  const [reactivateVendor, { loading: reactivating }] = useReactivateVendorMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted() {
      Alert.success('Vendor reactivated')
      refetch?.()
      setConfirmReactivate(false)
    }
  })

  const onConfirmDeactivate = async () => {
    await deactivateVendor({ variables: { id } })
  }

  const onConfirmReactivate = async () => {
    await reactivateVendor({ variables: { id } })
  }

  return (
    <Box>
      <IconButton
        ref={anchorRef[id]}
        id={`long-button-${id}`}
        aria-controls={`long-menu-${id}`}
        aria-haspopup="true"
        onClick={handleClick}
        aria-label="show more"
      >
        <Avatar sx={{ bgcolor: 'transparent' }}>
          <PrimaryDots />
        </Avatar>
      </IconButton>

      <Menu
        id={`long-menu-${id}`}
        anchorEl={anchorRef[id].current}
        open={open}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={handleClose}
      >
        {isInactive ? (
          <MenuItem
            onClick={() => {
              handleClose()
              setConfirmReactivate(true)
            }}
          >
            Reactivate
          </MenuItem>
        ) : (
          <>
            <MenuItem onClick={handleEdit}>View/Edit Details</MenuItem>

            <MenuItem
              onClick={() => {
                handleClose()
                setConfirmDeactivate(true)
              }}
            >
              Deactivate
            </MenuItem>
          </>
        )}
      </Menu>
      <VenderModal vendor={vendor} isOpen={isEditOpen} handleClose={handleEditClose} refetch={refetch} />

      <ConfirmationModal
        isOpen={confirmDeactivate}
        title="Deactivate vendor"
        message={`You are deactivating vendor "${vendor.name}" (${vendor.vendorId}). They will be hidden from normal use until reactivated.`}
        confirmText="Deactivate"
        onConfirm={onConfirmDeactivate}
        onCancel={() => setConfirmDeactivate(false)}
        variant="danger"
        isLoading={deactivating}
      />

      <ConfirmationModal
        isOpen={confirmReactivate}
        title="Reactivate vendor"
        message={`You are reactivating vendor "${vendor.name}" (${vendor.vendorId}).`}
        confirmText="Reactivate"
        onConfirm={onConfirmReactivate}
        onCancel={() => setConfirmReactivate(false)}
        variant="danger"
        isLoading={reactivating}
      />
    </Box>
  )
}

export default VendorMenu
